import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Chip, TableCell, TableRow, Typography } from "@mui/material";

import TruncateText from "../../../components/TruncateText";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";
import EmptyState from "../../../components/EmptyState";
import Filters from "../../../components/Filters";
import ListFilter from "../../../components/Filters/ListFilter";
import messages from "../../../assets/locale/messages";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import noImporterImg from "../../../assets/images/placeholders/noImportersPlaceholderImg.svg";
import {
  BUSINESS_FILTER_IDS,
  CHIPS_CLASSES
} from "../../../utils/Constants";
import { importersListRequest } from "../../../store/Users/actions";
import {
  categoriesLookupRequest,
  countriesLookupRequest
} from "../../../store/Lookups/actions";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";

const Importers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { users } = messages[lang];

  const importersList = useSelector(
    (state) => state.users.importers.list
  );
  const importersMeta = useSelector(
    (state) => state.users.importers.meta
  );
  const categoriesLookup = useSelector(
    (state) => state.lookups.categories.list
  );
  const countriesLookup = useSelector(
    (state) => state.lookups.countries.list
  );

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [categoryFilterValue, setCategoryFilterValue] = useState([]);
  const [countryFilterValue, setCountryFilterValue] = useState([]);
  const [businessFilterValue, setBusinessFilterValue] = useState([]);
  const [statusFilterValue, setStatusFilterValue] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [paramsObject, setParamsObject] = useState({
    country_ids: [],
    category_ids: [],
    business_type_ids: [],
    status_ids: []
  });

  useEffect(() => {
    dispatch(categoriesLookupRequest());
    dispatch(countriesLookupRequest());
  }, []);

  const requestImportersList = () => {
    dispatch(
      importersListRequest({
        params: {
          page,
          search_value: searchValue,
          ...paramsObject
        }
      })
    );
  };

  useEffect(() => {
    if (clearFilters) {
      setCategoryFilterValue([]);
      setCountryFilterValue([]);
      setBusinessFilterValue([]);
      setParamsObject({
        country_ids: [],
        category_ids: [],
        business_type_ids: [],
        status_ids: []
      });
      setClearFilters(false);
    }
  }, [clearFilters]);

  useEffect(() => {
    requestImportersList();
  }, [page, searchValue]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestImportersList();
  }, [paramsObject]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        country_ids: countryFilterValue,
        category_ids: categoryFilterValue,
        business_type_ids: businessFilterValue,
        isActive: statusFilterValue.includes(2)
          ? false
          : statusFilterValue.includes(1)
            ? true
            : null,
        isBlocked: statusFilterValue.includes(3)
      });
      setIsApplyFilters(false);
    }
  }, [isApplyFilters]);

  const BUSINESS_TYPE_FILTER = [
    {
      id: 1,
      value: users.businessFilter.factory
    },
    {
      id: 2,
      value: users.businessFilter.trade
    },
    {
      id: 3,
      value: users.businessFilter.both
    }
  ];

  const IMPORTER_STATUS_FILTER = [
    {
      id: 1,
      value: users.importersList.importerStatusFilter.active
    },
    {
      id: 2,
      value: users.importersList.importerStatusFilter.inactive
    },
    {
      id: 3,
      value: users.importersList.importerStatusFilter.blocked
    }
  ];

  const renderFilterContent = () => (
    <div className="filter-content-container h-100">
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={categoriesLookup}
          name="categories"
          label={users.importersList.filters.categories}
          searchPlaceholder={
            users.importersList.filters.searchPlaceholders.categories
          }
          selectedOptionsIds={categoryFilterValue}
          setSelectedOptionsIds={setCategoryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={BUSINESS_TYPE_FILTER.map((filter) => ({
            Id: filter.id,
            Name: filter.value
          }))}
          name="business type"
          label={users.importersList.filters.business}
          selectedOptionsIds={businessFilterValue}
          setSelectedOptionsIds={setBusinessFilterValue}
          clearFilters={clearFilters}
          collabsible
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={countriesLookup}
          name="countries"
          label={users.importersList.filters.countries}
          searchPlaceholder={
            users.importersList.filters.searchPlaceholders.countries
          }
          selectedOptionsIds={countryFilterValue}
          setSelectedOptionsIds={setCountryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className=" py-3 px-md-5 px-3">
        <ListFilter
          list={IMPORTER_STATUS_FILTER.map((filter) => ({
            Id: filter.id,
            Name: filter.value
          }))}
          name="status"
          label={users.importersList.filters.status}
          selectedOptionsIds={statusFilterValue}
          setSelectedOptionsIds={setStatusFilterValue}
          clearFilters={clearFilters}
          collabsible
          filterModalOpen={filterModalOpen}
          isSingleSelectionFilter
        />
      </div>
    </div>
  );

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const renderImportersTableRow = ({
    UserId,
    CompanyId,
    CompanyName,
    Country,
    BusinessType,
    CategoriesCount,
    IsActive,
    IsBlocked
  }) => (
    <TableRow
      className="pointer"
      key={`request-row-${UserId}`}
      onClick={() => {
        navigate(
          ROUTE_PATHS.usersImporterDetails.replace(":id", UserId)
        );
      }}>
      <TableCell style={{ width: "20%" }}>
        <TruncateText length={22} text={CompanyName || "__"} />
      </TableCell>
      <TableCell style={{ width: "20%" }}>
        <TruncateText text={Country || "__"} />
      </TableCell>
      <TableCell style={{ width: "20%" }}>
        <TruncateText text={BusinessType || "__"} />
      </TableCell>
      <TableCell style={{ width: "20%" }}>
        {`${
          CategoriesCount ?? "0"
        } ${users.importersList.categories}`}
      </TableCell>
      <TableCell style={{ width: "20%" }}>
        <Chip
          label={
            IsBlocked
              ? users.importersDetails.blocked
              : IsActive
                ? users.importersList.active
                : users.importersList.inactive
          }
          className={`${IsActive && !IsBlocked ? CHIPS_CLASSES.green : CHIPS_CLASSES.red}`}
          sx={{ fontWeight: "semibold", width: "100%" }}
        />
      </TableCell>
    </TableRow>
  );

  return (
    <div className="importers-list-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-md-center align-items-start">
        <div className="title-count-container d-flex justify-content-center align-items-md-center align-items-start flex-column flex-md-row">
          <div>
            <Typography variant="h5" className="title-color me-1">
              {users.importersList.importersListTitle}
            </Typography>
          </div>
          <div className="block-title text-secondary">
            <Typography>{`(${importersMeta?.count ? importersMeta?.count : "0"} ${importersMeta?.count === 1 ? users.importersList.importer : users.importersList.importers})`}</Typography>
          </div>
        </div>
        <div className="title-buttons-container">
          <Filters
            handleClearFilters={setClearFilters}
            handleApplyFilters={setIsApplyFilters}
            FilterContent={renderFilterContent()}
            setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
          />
        </div>
      </div>
      <div className="importers-table-container bg-white d-flex flex-column w-100  p-4 pb-0 px-0 border-radius-8">
        <div className="table-header d-flex px-4 align-items-center w-100 mb-4">
          <SearchInput
            name="ImporterSearch"
            id="ImporterSearch"
            value={searchValue}
            inputClass="border-radius-8"
            inputWrapperClass=" search-input-wrapper search-input-wrapper-width small-placeholder-search"
            placeholder={users.importersList.searchPlaceholder}
            onChange={(value) => {
              setPage(1);
              setSearchValue(value);
            }}
          />
        </div>
        <div className="table-content">
          <div>
            {importersList?.length ? (
              <Table
                className={"px-0 nowrap-table status-action-column"}
                headlines={[
                  users.importersList.tableHeader.companyName,
                  users.importersList.tableHeader.country,
                  users.importersList.tableHeader.businessType,
                  users.importersList.tableHeader.category,
                  users.importersList.tableHeader.status
                ]}
                rows={
                  !isLoading &&
                  importersList &&
                  importersList.map((importer) =>
                    renderImportersTableRow(importer)
                  )
                }
              />
            ) : (
              <div className="border-top">
                {!isLoading && (
                  <EmptyState
                    subTitle={
                      !!searchValue && !importersList.length
                        ? users.importersList.noSearchResults
                        : users.importersList.noImportersFound
                    }
                    imgSrc={noImporterImg}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
          {!isLoading && importersList.length > 0 && (
            <Pagination
              count={importersMeta?.pages}
              page={page || importersMeta.currentPage}
              handleChange={(page) => {
                setPage(page);
              }}
              defaultPage={1}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Importers;
